import {Trans} from '@lingui/macro';
import React, {FC, useRef, useState} from 'react';
import styled from 'styled-components';

import {Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {BoxedInput} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedInput';
import {BoxedTextAreaElement} from '../../../../libs/shared/ui/src/components/boxedInputs/boxedTextAreaElement';
import {Button} from '../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroup} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {TextArea} from '../../../../libs/shared/ui/src/components/inputs/textarea';
import {PopoverCoordinator} from '../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {UserThemeConsumer} from '../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {computeIsGlobalNamespace, isTeammateNamespace} from '../../../helpers/models/namespaceHelpers';
import {TeamModel} from '../../../models/api/teamModel';
import {TeamAvatar} from '../../common/avatars/teamAvatar';
import {NamespaceDropdown} from '../../common/dropdowns/namespace/namespaceDropdown';
import {
  InlineEditingError,
  InlineEditingErrorMessage,
} from '../../common/inlineEditing/inlineEditingErrorMessage';

/*
 * Constants.
 */

const BoxedTextAreaElementStyle: React.CSSProperties = {
  minHeight: `84px`,
  width: 'calc(100% - 16px)',
};

const AccessDropdownWidth = 222;
const AccessDropdownHeight = 300;

/*
 * Props.
 */

export interface ComposerNotePartial {
  namespace: string;
  text: string;
}

interface ContactNoteComposerProps {
  userNamespace: string;
  value: ComposerNotePartial;
  onEdit: HandlerOf<ComposerNotePartial>;
  onEditSave: HandlerOf<ComposerNotePartial>;
  onEditCancel: Handler;
  isSaving: boolean;
  className?: string;
  teams: ReadonlyArray<TeamModel>;
  isEditingExistingNote?: boolean;
  error: InlineEditingError;
  shouldOnlyShowPrivateNamespace?: boolean;
}

/*
 * Styles.
 */

const StyledBoxContentDiv = styled.div`
  display: flex;
  flex-flow: column;
  flex: 1;
`;

const StyledButtonGroup = styled(ButtonGroup)`
  padding: 2px 4px;
`;

const StyledBoxedTextAreaElement = styled(BoxedTextAreaElement)`
  resize: vertical;
  flex: unset;
  padding: 4px 5px 5px 5px;
`;

const StyledButton = styled(Button)`
  margin: 0 4px 3px 4px;
`;

const StyledNamespaceButton = styled(StyledButton)`
  margin-left: 2px;
`;

const StyledSaveButton = styled(StyledButton)`
  margin-right: 7px;
`;

const StyledButtonContentDiv = styled.div`
  display: flex;
  flex-flow: row;
  /* Remove some margin around the dropdown content */
  margin: 0 -3px;
`;

const StyledChevonIcon = styled(Icon)`
  margin-left: 4px;
`;

/*
 * Component.
 */

export const ContactNoteComposer: FC<ContactNoteComposerProps> = (props) => {
  const {
    className,
    value,
    userNamespace,
    teams,
    error,
    onEdit,
    onEditCancel,
    onEditSave,
    isSaving,
    isEditingExistingNote,
    shouldOnlyShowPrivateNamespace,
  } = props;

  const [shouldJumpToEnd, setShouldJumpToEnd] = useState(value.text !== '');
  const textAreaRef = useRef<React.ElementRef<typeof TextArea>>(null);

  return (
    <BoxedInput
      shouldAutoFocus
      noMargin
      boxClassName={className}
      hasError={Boolean(error)}
      errorMessage={<InlineEditingErrorMessage error={error} />}
    >
      {(childProps) => (
        <StyledBoxContentDiv>
          <StyledBoxedTextAreaElement
            {...childProps}
            textAreaRef={textAreaRef}
            style={BoxedTextAreaElementStyle}
            value={value.text}
            onChange={(text) => onEdit({...value, text})}
            onFocus={() => {
              if (shouldJumpToEnd && textAreaRef.current) {
                setShouldJumpToEnd(false);
                textAreaRef.current.focus(false, true);
              }
            }}
            isDisabled={isSaving}
            shouldAllowResize
            interactionId="contact-details-note-compose-textarea"
          />
          <StyledButtonGroup>
            {!isEditingExistingNote &&
              !shouldOnlyShowPrivateNamespace &&
              renderNoteNamespaceDropdownButton(value, teams, isSaving, userNamespace, onEdit)}
            <ButtonGroupSpacer />
            <StyledButton
              onClick={onEditCancel}
              size={VisualSizesEnum.SMALL}
              isDisabled={isSaving}
              interactionId="contact-details-note-compose-actions-cancel-btn"
            >
              <Trans id="contact-details-note-compose-actions-cancel">Cancel</Trans>
            </StyledButton>
            <StyledSaveButton
              size={VisualSizesEnum.SMALL}
              variant={ButtonVariantsEnum.PRIMARY}
              isDisabled={isSaving}
              onClick={async () => {
                await onEditSave(value);
              }}
              interactionId="contact-details-note-compose-actions-save-btn"
            >
              <Trans id="contact-details-note-compose-actions-save">Save</Trans>
            </StyledSaveButton>
          </StyledButtonGroup>
        </StyledBoxContentDiv>
      )}
    </BoxedInput>
  );
};

const renderNoteNamespace = (note: ComposerNotePartial, teams: ReadonlyArray<TeamModel>) => {
  if (isTeammateNamespace(note.namespace)) {
    return (
      <UserThemeConsumer>
        {(theme) => <Icon name="lockSmall" size={16} color={theme.greys.shade70} />}
      </UserThemeConsumer>
    );
  }

  if (computeIsGlobalNamespace(note.namespace)) {
    return (
      <UserThemeConsumer>
        {(theme) => <Icon name="buildingFilledSmall" size={16} color={theme.palette.teal.shade40} />}
      </UserThemeConsumer>
    );
  }

  const team = teams.find((t) => t.namespace === note.namespace);
  if (team) {
    return <TeamAvatar team={team} size={VisualSizesEnum.SMALL} />;
  }

  // Display a question mark as this should not happen
  return (
    <UserThemeConsumer>
      {(theme) => <Icon name="questionmarkMaybeSmall" size={16} color={theme.greys.shade70} />}
    </UserThemeConsumer>
  );
};

const renderNoteNamespaceDropdownButton = (
  note: ComposerNotePartial,
  teams: ReadonlyArray<TeamModel>,
  isSaving: boolean,
  userNamespace: string,
  onEdit: HandlerOf<ComposerNotePartial>,
) => {
  const isDisabled = isSaving;
  return (
    <UserThemeConsumer>
      {(theme) => (
        <PopoverCoordinator
          isDisabled={isDisabled}
          renderButton={() => (
            <TooltipCoordinator render={() => getAccessTitle()} placement="top">
              <StyledNamespaceButton size={VisualSizesEnum.SMALL} isDisabled={isDisabled}>
                <StyledButtonContentDiv>
                  {renderNoteNamespace(note, teams)}
                  <StyledChevonIcon name="chevronDownSmall" color={theme.greys.shade60} size={12} />
                </StyledButtonContentDiv>
              </StyledNamespaceButton>
            </TooltipCoordinator>
          )}
          renderModal={(popoverProps) => (
            <NamespaceDropdown
              {...popoverProps}
              teams={teams}
              width={AccessDropdownWidth}
              maxHeight={AccessDropdownHeight}
              selectedNamespace={note.namespace}
              userNamespace={userNamespace}
              header={getAccessTitle()}
              onNamespaceSelect={(namespace) => onEdit({...note, namespace})}
              shouldRenderCompanyDropdownItem
              shouldRenderVisibilityDescription
            />
          )}
        />
      )}
    </UserThemeConsumer>
  );
};

const getAccessTitle = () => <Trans id="contact-details-note-compose-actions-namespace-title">Access</Trans>;
